import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const CONSENT_KEY = 'infraverge-cookie-consent';

type ConsentStatus = 'accepted' | 'rejected' | null;

const CookieBanner: React.FC = () => {
  const [consent, setConsent] = useState<ConsentStatus>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(CONSENT_KEY) as ConsentStatus | null;
      if (stored === 'accepted' || stored === 'rejected') {
        setConsent(stored);
      }
    } catch {
      // localStorage not available
    }
    setMounted(true);
  }, []);

  const saveConsent = (value: 'accepted' | 'rejected') => {
    try {
      localStorage.setItem(CONSENT_KEY, value);
      setConsent(value);
    } catch {
      setConsent(value);
    }
  };

  if (!mounted || consent !== null) return null;

  return (
    <AnimatePresence>
      <motion.div
        role="dialog"
        aria-label="Cookie consent"
        aria-live="polite"
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="fixed bottom-0 left-0 right-0 z-[100] p-4 sm:p-5 md:p-6"
      >
        <div className="max-w-4xl mx-auto rounded-2xl border border-slate-200 bg-white shadow-lg shadow-slate-900/10 p-4 sm:p-5 md:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="min-w-0 pr-0 sm:pr-4">
              <h3 className="text-sm font-semibold text-slate-900 mb-1">
                We use cookies
              </h3>
              <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                We use necessary cookies to run the site. Optional cookies help us improve your experience and analyse traffic. You can accept all, or reject non-essential cookies. See our{' '}
                <Link to="/privacy" className="text-blue-600 hover:text-blue-700 underline underline-offset-2 font-medium">
                  Privacy Policy
                </Link>
                {' '}for details.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 shrink-0">
              <button
                type="button"
                onClick={() => saveConsent('rejected')}
                className="order-2 sm:order-1 min-h-[44px] inline-flex items-center justify-center px-4 py-3 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors border border-slate-200"
              >
                Reject non-essential
              </button>
              <button
                type="button"
                onClick={() => saveConsent('accepted')}
                className="order-1 sm:order-2 min-h-[44px] inline-flex items-center justify-center px-4 py-3 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm"
              >
                Accept all
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieBanner;
